import React, { useState, useEffect } from 'react';
import './TypewriterText.css';

/**
 * Typewriter-style animated text component
 * Cycles through a list of phrases with typing and deleting animation
 * Supports a static prefix that remains visible while cycling through suffixes
 */
const TypewriterText = ({
  prefix = '',
  phrases = [],
  typingSpeed = 80,
  deletingSpeed = 40,
  pauseDuration = 2000,
  className = ''
}) => {
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (phrases.length === 0) return;

    const currentPhrase = phrases[currentPhraseIndex];

    const handleTyping = () => {
      if (isPaused) {
        // After pause, start deleting
        const pauseTimer = setTimeout(() => {
          setIsPaused(false);
          setIsDeleting(true);
        }, pauseDuration);
        return () => clearTimeout(pauseTimer);
      }

      if (isDeleting) {
        // Deleting characters (only the suffix, not the prefix)
        if (displayText.length > 0) {
          const deleteTimer = setTimeout(() => {
            setDisplayText(prev => prev.slice(0, -1));
          }, deletingSpeed);
          return () => clearTimeout(deleteTimer);
        } else {
          // Done deleting, move to next phrase
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
        }
      } else {
        // Typing characters
        if (displayText.length < currentPhrase.length) {
          const typeTimer = setTimeout(() => {
            setDisplayText(currentPhrase.slice(0, displayText.length + 1));
          }, typingSpeed);
          return () => clearTimeout(typeTimer);
        } else {
          // Done typing, pause before deleting
          setIsPaused(true);
        }
      }
    };

    const cleanup = handleTyping();
    return cleanup;
  }, [displayText, isDeleting, isPaused, currentPhraseIndex, phrases, typingSpeed, deletingSpeed, pauseDuration]);

  return (
    <span className={`typewriter-container ${className}`}>
      <span className="typewriter-prefix">{prefix}</span>
      <span className="typewriter-text">{displayText}</span>
      <span className="typewriter-cursor">|</span>
    </span>
  );
};

export default TypewriterText;


